import type {
  DataExtensionSchema,
  RequestedField,
} from '@wix/bex-utils/@wix/ambassador-data-extensions-v1-data-extension-schema/types';
import {
  createDataExtensionSchema,
  listDataExtensionSchemas,
  updateDataExtensionSchema,
  deleteUserDefinedFields,
} from '@wix/bex-utils/@wix/ambassador-data-extensions-v1-data-extension-schema/http';
import { CustomFieldForm, WixPatternsContainer } from '../../state';
import { PatternsErrorHandler, WixPatternsEssentials } from '../../model';
import { updateDataExtensionSchemaErrors } from './updateDataExtensionSchemaErrors';
import { createDataExtensionSchemaErrors } from './createDataExtensionSchemaErrors';
import { deleteUserDefinedFieldsErrors } from './deleteUserDefinedFieldsErrors';

export interface DataExtensionSchemaServiceParams {
  readonly httpClient: WixPatternsEssentials['httpClient'];
  readonly translate: WixPatternsContainer['translate'];
  readonly errorHandler: PatternsErrorHandler;
}

// server types
export type DataExtensionType =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'array'
  | 'object';

export type DataExtensionPermission =
  | 'apps'
  | 'owning-app'
  | 'users'
  | 'users-of-users';

interface DataExtensionFieldItems
  extends Partial<Omit<DataExtensionField, 'type' | 'maxLength'>> {
  type: Omit<DataExtensionType, 'array'>;
  maxLength: number;
}
export interface DataExtensionField {
  id: string;
  type?: DataExtensionType;
  format?: 'date-time' | 'date' | 'single-line' | 'uri' | string;
  title?: string;
  maxLength?: number;
  enum?: string[];
  'x-wix-archived'?: boolean;
  'x-wix-filterable'?: boolean;
  'x-wix-created-date': string;
  'x-wix-object-type'?: string;
  'x-wix-permissions': {
    read: DataExtensionPermission[];
    write: DataExtensionPermission[];
  };
  properties?: Record<string, any>;
  items?: DataExtensionFieldItems;
  'x-wix-pii'?: { enabled: boolean };
}

// client types
export type CustomFieldType =
  | 'shortText'
  | 'longText'
  | 'url'
  | 'date'
  | 'dateTime'
  | 'integer'
  | 'decimal'
  | 'checkbox'
  | 'dropdown'
  | 'files'
  | 'multiSelect';

export type CustomFieldOrigin =
  | 'Custom columns panel'
  | 'Custom fields panel'
  | 'Custom fields widget'
  | 'external';

export interface ExtendedFieldsDataMap {
  [key: string]: any;
}

export interface ExtendedFields {
  _type?: 'ExtendedFields';
  /**
   * Data Extensions extended fields
   * Key: Namespace
   * Value: extended fields data in Struct format
   */
  namespaces?: {
    _user_fields?: ExtendedFieldsDataMap | undefined;
    [key: string]: ExtendedFieldsDataMap | undefined;
  };
}

export type CustomFieldOwner = 'user' | 'app';

export interface CustomField {
  id: string;
  type: CustomFieldType;
  label?: string;
  maxLength?: number;
  options?: string[];
  editable: boolean;
  filterable?: boolean;
  owner: CustomFieldOwner;
  properties?: Record<string, string>;
  namespace: string;
  isPii?: boolean;
  permissions?: {
    read?: DataExtensionPermission[];
    write?: DataExtensionPermission[];
  };
}

export interface AddFieldParams {
  schema?: DataExtensionSchema;
  fqdn: string;
  form: CustomFieldForm;
  filterable?: boolean;
}

export interface ArchiveFieldParams {
  schema?: DataExtensionSchema;
  fqdn: string;
  id: string;
}

export class DataExtensionSchemaService {
  readonly httpClient: WixPatternsEssentials['httpClient'];
  readonly translate: WixPatternsContainer['translate'];
  readonly errorHandler;

  readonly userFieldsNamespace = '_user_fields';

  static toSchemaType(type?: CustomFieldType) {
    switch (type) {
      case 'shortText':
        return {
          type: 'string',
          format: 'single-line',
          maxLength: 120,
        };
      case 'longText':
        return {
          type: 'string',
          maxLength: 200,
        };
      case 'url':
        return {
          type: 'string',
          format: 'uri',
          maxLength: 256,
        };
      case 'date':
        return {
          type: 'string',
          format: 'date',
        };
      case 'dateTime':
        return {
          type: 'string',
          format: 'date-time',
        };
      case 'integer':
        return {
          type: 'integer',
        };
      case 'decimal':
        return {
          type: 'number',
        };
      case 'checkbox':
        return {
          type: 'boolean',
        };
      case 'dropdown':
        return {
          type: 'array',
          items: {
            type: 'string',
          },
        };
      default:
        throw new Error(`Unexpected type: ${type}`);
    }
  }

  static toCustomType(field: DataExtensionField): CustomFieldType | undefined {
    switch (field.type) {
      case 'string':
        if (field.hasOwnProperty('enum')) {
          return 'dropdown';
        }
        switch (field.format) {
          case 'date-time':
            return 'dateTime';
          case 'uri':
            return 'url';
          case 'date':
            return 'date';
          case 'single-line':
            return 'shortText';
          default:
            return 'longText';
        }
      case 'number':
        return 'decimal';
      case 'integer':
        return 'integer';
      case 'boolean':
        return 'checkbox';
      case 'array': {
        if (field.items?.['x-wix-object-type'] === 'FILE/V1') {
          return 'files';
        }
        return 'multiSelect';
      }
      case 'object': {
        if (field['x-wix-object-type'] === 'FILE/V1') {
          return 'files';
        }
        return 'dropdown';
      }
      default:
        return;
    }
  }

  static toCustomField(
    schema: DataExtensionSchema,
    key: string,
  ): DataExtensionField | undefined {
    const property: DataExtensionField = schema.jsonSchema?.properties?.[key];

    if (!property || typeof property !== 'object') {
      return;
    }

    if (
      typeof property.title !== 'string' ||
      typeof property.type !== 'string' ||
      typeof property['x-wix-permissions'] !== 'object'
    ) {
      return;
    }

    return { ...property, id: key };
  }

  constructor(params: DataExtensionSchemaServiceParams) {
    this.httpClient = params.httpClient;
    this.translate = params.translate;
    this.errorHandler = params.errorHandler;
  }
  addCustomField(params: AddFieldParams) {
    const { userFieldsNamespace } = this;
    const {
      form: {
        key,
        name,
        readApps,
        readUsers,
        writeApps,
        writeUsers,
        containsPii,
      },
      fqdn,
    } = params;
    const type = DataExtensionSchemaService.toSchemaType(params.form.type);

    const permissions = {
      read: [
        'users',
        ...(readUsers ? ['users-of-users'] : []),
        ...(readApps ? ['apps'] : []),
      ],
      write: [
        'users',
        ...(writeUsers ? ['users-of-users'] : []),
        ...(writeApps ? ['apps'] : []),
      ],
    };

    const filterable = params.filterable ? { 'x-wix-filterable': true } : {};

    const schema: DataExtensionSchema = {
      fqdn,
      namespace: userFieldsNamespace,
      ...params.schema,
      jsonSchema: {
        properties: {
          ...params.schema?.jsonSchema?.properties,
          [key]: {
            ...params.schema?.jsonSchema?.properties?.[key],
            title: name,
            ...(containsPii ? { 'x-wix-pii': { enabled: true } } : {}),
            ...type,
            'x-wix-permissions': permissions,
            ...filterable,
          },
        },
      },
    };

    return params.schema?.id
      ? this._updateDataExtensionSchema(schema)
      : this._createDataExtensionSchema(schema);
  }

  archiveCustomField(params: ArchiveFieldParams) {
    const { userFieldsNamespace } = this;
    const { id, fqdn } = params;

    const schema: DataExtensionSchema = {
      fqdn,
      namespace: userFieldsNamespace,
      ...params.schema,
      jsonSchema: {
        properties: {
          ...params.schema?.jsonSchema?.properties,
          [id]: {
            ...params.schema?.jsonSchema?.properties?.[id],
            'x-wix-archived': true,
          },
        },
      },
    };
    return this._updateDataExtensionSchema(schema);
  }

  restoreCustomField(params: ArchiveFieldParams) {
    const { userFieldsNamespace } = this;
    const { id, fqdn } = params;

    const schema: DataExtensionSchema = {
      fqdn,
      namespace: userFieldsNamespace,
      ...params.schema,
      jsonSchema: {
        properties: {
          ...params.schema?.jsonSchema?.properties,
          [id]: {
            ...params.schema?.jsonSchema?.properties?.[id],
            'x-wix-archived': false,
          },
        },
      },
    };
    return this._updateDataExtensionSchema(schema);
  }

  restoreDeletedField(params: ArchiveFieldParams & { fieldData: any }) {
    const { userFieldsNamespace } = this;
    const { id, fqdn, fieldData } = params;

    const schema: DataExtensionSchema = {
      fqdn,
      namespace: userFieldsNamespace,
      ...params.schema,
      jsonSchema: {
        properties: {
          ...params.schema?.jsonSchema?.properties,
          [id]: {
            ...fieldData,
            'x-wix-archived': true,
          },
        },
      },
    };
    return this._updateDataExtensionSchema(schema);
  }

  deleteCustomField(params: ArchiveFieldParams) {
    const { id } = params;

    if (!params.schema?.id) {
      throw new Error('Schema ID is required to delete a custom field');
    }

    // Check if the field is archived before attempting deletion
    const field = params.schema.jsonSchema?.properties?.[id];
    if (field && !field['x-wix-archived']) {
      throw new Error(
        'Field must be archived before it can be deleted. Please archive the field first.',
      );
    }

    return this._deleteUserDefinedFields(params.schema.id, [id]);
  }

  async fetchSchemas(fqdn: string) {
    const { httpClient, errorHandler } = this;

    const res = await errorHandler.withErrorHandler(
      () =>
        httpClient.request(
          listDataExtensionSchemas({
            fqdn,
            namespaces: [],
            fields: ['ARCHIVED' as RequestedField],
          }),
        ),
      {},
    );

    return res.data;
  }

  private async _createDataExtensionSchema(schema: DataExtensionSchema) {
    const { httpClient, errorHandler } = this;

    return errorHandler
      .withErrorHandler(
        () =>
          httpClient.request(
            createDataExtensionSchema({ dataExtensionSchema: schema }),
          ),
        createDataExtensionSchemaErrors({
          translate: this.translate,
        }),
      )
      .then((res) => res.data);
  }

  private async _updateDataExtensionSchema(schema: DataExtensionSchema) {
    const { httpClient, errorHandler } = this;
    return errorHandler
      .withErrorHandler(
        () =>
          httpClient.request(
            updateDataExtensionSchema({ dataExtensionSchema: schema }),
          ),
        updateDataExtensionSchemaErrors({
          translate: this.translate,
        }),
      )
      .then((res) => res.data);
  }

  private async _deleteUserDefinedFields(
    dataExtensionSchemaId: string,
    fieldsToDelete: string[],
  ) {
    const { httpClient, errorHandler } = this;
    return errorHandler
      .withErrorHandler(
        () =>
          httpClient.request(
            deleteUserDefinedFields({
              dataExtensionSchemaId,
              fieldsToDelete,
            }),
          ),
        deleteUserDefinedFieldsErrors({
          translate: this.translate,
        }),
      )
      .then((res) => res.data);
  }
}
